/**
 * Onboarding Payout — IN-2.d
 * ──────────────────────────
 * Wizard-side wrapper that captures the contractor's payout method on
 * the `OnboardingSession` (not on a production Contractor row).
 *
 * Reuses the IN-1.c writer `setPayoutMethod()` via a synthetic
 * Contractor wrapper so the entity-type ↔ bankingMode validation and
 * the raw-bank-key guard are shared. Nothing here writes to a real
 * Contractor; the IN-2.g bridge will lift the validated shape onto the
 * real record.
 *
 * Hard rules (IN-2.d):
 *   • No raw routing / account numbers — `setPayoutMethod` blocks them.
 *   • `last4` is the only sensitive surface allowed.
 *   • Tokenized placeholder only; no Stripe / Plaid / Dwolla SDK.
 *   • `verified=false` by default; beta may flip via `placeholderVerified`
 *     so the review step can clear soft-blockers, but this writes only
 *     to the session (never to a contractor).
 *   • Entity-type compatibility is enforced (independent/licensed/
 *     manufacturer_partner → own; internal_w2 → payroll; branch/crew/
 *     helper → parent_routed with parentEntityId).
 */

import {
  setPayoutMethod,
  validatePayoutMethodForEntity,
  type SetPayoutMethodInput,
} from "@/data/contractor/compliance/payoutMethod";
import type { Contractor } from "@/data/types";
import type { OnboardingSession } from "./types";

export type OnboardingPayoutType =
  | "ach"
  | "check"
  | "stripe_connect"
  | "parent_routed"
  | "payroll";

export interface SetOnboardingPayoutInput {
  type: OnboardingPayoutType;
  last4?: string;
  parentEntityId?: string;
  /**
   * Beta-only: when true, marks the placeholder as "verified" so the
   * review step does not surface a soft-blocker. No real verification
   * happens. The bridge in IN-2.g will reset this to false until a real
   * processor confirms.
   */
  placeholderVerified?: boolean;
}

/** Pure: returns a new OnboardingSession with payoutMethod set. */
export function setOnboardingPayout(
  session: OnboardingSession,
  input: SetOnboardingPayoutInput,
  actor: string,
): OnboardingSession {
  if (!session) {
    throw new Error("onboardingPayout: session is required.");
  }

  // Build a synthetic Contractor so we can reuse the IN-1.c writer's
  // entity-type ↔ bankingMode validation + raw-bank-key guard.
  const synthetic = {
    id: session.id,
    entityType: session.entityType,
    parentEntityId: session.parentEntityId ?? null,
    payoutMethod: session.payoutMethod,
  } as unknown as Contractor;

  const writerInput: SetPayoutMethodInput = {
    type: input.type,
    last4: input.last4,
    parentEntityId: input.parentEntityId,
    verified: Boolean(input.placeholderVerified),
  };

  const next = setPayoutMethod(synthetic, writerInput, actor);
  return {
    ...session,
    payoutMethod: next.payoutMethod,
    lastTouchedAt: new Date().toISOString(),
  };
}

/** True when the session has a payoutMethod whose type matches the
 *  required bankingMode for its entity type. */
export function hasValidOnboardingPayout(
  session: Pick<
    OnboardingSession,
    "entityType" | "parentEntityId" | "payoutMethod"
  >,
): boolean {
  if (!session.payoutMethod) return false;
  // Re-run the IN-1.c validator against a synthetic contractor.
  const synthetic = {
    id: "ob-check",
    entityType: session.entityType,
    parentEntityId: session.parentEntityId ?? null,
    payoutMethod: session.payoutMethod,
  } as unknown as Contractor;
  return validatePayoutMethodForEntity(synthetic, session.payoutMethod).length === 0;
}
